import { goRequest } from '../utils/httpClientTs';
import {
  BedAndBreakfast,
  BnBListResponse,
  BnBDetailResponse,
  BnBCreateRequest,
  BnBUpdateRequest,
  BnBContact,
  BnBContactCreateRequest,
  BnBContactUpdateRequest,
  BnBFee,
  BnBFeeCreateRequest,
  BnBFeeUpdateRequest,
  BnBBooking,
  BnBBookingCreateRequest,
  BnBBookingUpdateRequest,
  BnBAvailabilityResponse,
  BnBSearchParams,
  BnBPrice,
  BnBPriceCreateRequest,
  BnBPriceUpdateRequest,
  BnBMessageRequest,
} from '../types/bedandbreakfast';
import { GenericResponse, GenericResponseWithId } from '../types/generic';

// GET /bed-and-breakfast/list - Get list of bed and breakfasts
export const getBedAndBreakfasts = (
  params: BnBSearchParams
): Promise<BnBListResponse> => {
  return goRequest<BnBListResponse>({
    url: '/bed-and-breakfast/list',
    method: 'GET',
    params,
  });
};

// GET /bed-and-breakfast/:id - Get bed and breakfast details
export const getBedAndBreakfast = (id: string): Promise<BnBDetailResponse> => {
  return goRequest<BnBDetailResponse>({
    url: `/bed-and-breakfast/${id}`,
    method: 'GET',
  });
};

// POST /bed-and-breakfast - Create bed and breakfast
export const createBedAndBreakfast = (
  data: BnBCreateRequest
): Promise<GenericResponseWithId> => {
  return goRequest<GenericResponseWithId>({
    url: '/bed-and-breakfast/create',
    method: 'POST',
    data,
  });
};

// PUT /bed-and-breakfast/:id - Update bed and breakfast
export const updateBedAndBreakfast = (
  id: string,
  data: BnBUpdateRequest
): Promise<BedAndBreakfast> => {
  return goRequest<BedAndBreakfast>({
    url: `/bed-and-breakfast/${id}`,
    method: 'PUT',
    data,
  });
};

// DELETE /bed-and-breakfast/:id - Delete bed and breakfast
export const deleteBedAndBreakfast = (id: string): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/bed-and-breakfast/${id}`,
    method: 'DELETE',
  });
};

// Contact management
// GET /bed-and-breakfast/:id/contacts - Get bed and breakfast contacts
export const getBnBContacts = (bnbId: string): Promise<BnBContact[]> => {
  return goRequest<BnBContact[]>({
    url: `/bed-and-breakfast/${bnbId}/contacts`,
    method: 'GET',
  });
};

// POST /bed-and-breakfast/:id/contacts - Create bed and breakfast contact
export const createBnBContact = (
  bnbId: string,
  data: BnBContactCreateRequest
): Promise<BnBContact> => {
  return goRequest<BnBContact>({
    url: `/bed-and-breakfast/${bnbId}/contacts`,
    method: 'POST',
    data,
  });
};

// PUT /bed-and-breakfast/:id/contacts/:contactId - Update bed and breakfast contact
export const updateBnBContact = (
  bnbId: string,
  contactId: string,
  data: BnBContactUpdateRequest
): Promise<BnBContact> => {
  return goRequest<BnBContact>({
    url: `/bed-and-breakfast/${bnbId}/contacts/${contactId}`,
    method: 'PUT',
    data,
  });
};

// DELETE /bed-and-breakfast/:id/contacts/:contactId - Delete bed and breakfast contact
export const deleteBnBContact = (
  bnbId: string,
  contactId: string
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/bed-and-breakfast/${bnbId}/contacts/${contactId}`,
    method: 'DELETE',
  });
};

// Fee management
// GET /bed-and-breakfast/:id/fees - Get bed and breakfast fees
export const getBnBFees = (bnbId: string): Promise<BnBFee[]> => {
  return goRequest<BnBFee[]>({
    url: `/bed-and-breakfast/${bnbId}/fees`,
    method: 'GET',
  });
};

// POST /bed-and-breakfast/:id/fees - Create bed and breakfast fee
export const createBnBFee = (
  bnbId: string,
  data: BnBFeeCreateRequest
): Promise<BnBFee> => {
  return goRequest<BnBFee>({
    url: `/bed-and-breakfast/${bnbId}/fees`,
    method: 'POST',
    data,
  });
};

// PUT /bed-and-breakfast/:id/fees/:feeId - Update bed and breakfast fee
export const updateBnBFee = (
  bnbId: string,
  feeId: string,
  data: BnBFeeUpdateRequest
): Promise<BnBFee> => {
  return goRequest<BnBFee>({
    url: `/bed-and-breakfast/${bnbId}/fees/${feeId}`,
    method: 'PUT',
    data,
  });
};

// DELETE /bed-and-breakfast/:id/fees/:feeId - Delete bed and breakfast fee
export const deleteBnBFee = (
  bnbId: string,
  feeId: string
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/bed-and-breakfast/${bnbId}/fees/${feeId}`,
    method: 'DELETE',
  });
};

// Booking management
// GET /bed-and-breakfast/:id/bookings - Get bed and breakfast bookings
export const getBnBBookings = (bnbId: string): Promise<BnBBooking[]> => {
  return goRequest<BnBBooking[]>({
    url: `/bed-and-breakfast/${bnbId}/bookings`,
    method: 'GET',
  });
};

// POST /bed-and-breakfast/:id/bookings - Create bed and breakfast booking
export const createBnBBooking = (
  bnbId: string,
  data: BnBBookingCreateRequest
): Promise<BnBBooking> => {
  return goRequest<BnBBooking>({
    url: `/bed-and-breakfast/${bnbId}/bookings`,
    method: 'POST',
    data,
  });
};

// PUT /bed-and-breakfast/:id/bookings/:bookingId - Update bed and breakfast booking
export const updateBnBBooking = (
  bnbId: string,
  bookingId: string,
  data: BnBBookingUpdateRequest
): Promise<BnBBooking> => {
  return goRequest<BnBBooking>({
    url: `/bed-and-breakfast/${bnbId}/bookings/${bookingId}`,
    method: 'PUT',
    data,
  });
};

// DELETE /bed-and-breakfast/:id/bookings/:bookingId - Delete bed and breakfast booking
export const deleteBnBBooking = (
  bnbId: string,
  bookingId: string
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/bed-and-breakfast/${bnbId}/bookings/${bookingId}`,
    method: 'DELETE',
  });
};

// Availability check - now uses bookings data instead of separate endpoint
export const checkBnBAvailability = (
  bnbId: string,
  checkInDate: string,
  checkOutDate: string
): Promise<BnBAvailabilityResponse> => {
  return goRequest<BnBAvailabilityResponse>({
    url: `/bed-and-breakfast/${bnbId}/availability`,
    method: 'GET',
    params: { check_in_date: checkInDate, check_out_date: checkOutDate },
  });
};

// Pricing management
// GET /bed-and-breakfast/:id/pricing - Get bed and breakfast pricing
export const getBnBPricing = (bnbId: string): Promise<BnBPrice[]> => {
  return goRequest<BnBPrice[]>({
    url: `/bed-and-breakfast/${bnbId}/pricing`,
    method: 'GET',
  });
};

// POST /bed-and-breakfast/:id/pricing - Create bed and breakfast pricing
export const createBnBPricing = (
  bnbId: string,
  data: BnBPriceCreateRequest
): Promise<BnBPrice> => {
  return goRequest<BnBPrice>({
    url: `/bed-and-breakfast/${bnbId}/pricing`,
    method: 'POST',
    data,
  });
};

// PUT /bed-and-breakfast/:id/pricing/:priceId - Update bed and breakfast pricing
export const updateBnBPricing = (
  bnbId: string,
  priceId: string,
  data: BnBPriceUpdateRequest
): Promise<BnBPrice> => {
  return goRequest<BnBPrice>({
    url: `/bed-and-breakfast/${bnbId}/pricing/${priceId}`,
    method: 'PUT',
    data,
  });
};

// DELETE /bed-and-breakfast/:id/pricing/:priceId - Delete bed and breakfast pricing
export const deleteBnBPricing = (
  bnbId: string,
  priceId: string
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/bed-and-breakfast/${bnbId}/pricing/${priceId}`,
    method: 'DELETE',
  });
};

// Message management
// POST /bed-and-breakfast/:id/send-message - Send message to bed and breakfast
export const sendBnBMessage = (
  bnbId: string,
  data: BnBMessageRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/bed-and-breakfast/${bnbId}/send-message`,
    method: 'POST',
    data,
  });
};
